import { Fragment, useState, useContext } from "react";
import { Dialog, Popover, Transition } from "@headlessui/react";
import {
  Bars3Icon,
  ShoppingBagIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import ShoppingCart from "../components/shoppingCart/ShoppingCart";
import ShoppingCartProductContext from "../components/shoppingCart/ShoppingCartProductContext";
import { Link } from "react-router-dom";

const navigation = {
  pages: [
    {
      id: "women",
      name: "WOMAN",
      Link: "types/e1019b39-1d60-432c-817c-6b52a4ab0b17",
    },
    {
      id: "men",
      name: "MAN",
      Link: "types/c6a2851f-c427-4915-93c6-75c674b98664",
    },
    { name: "KIDS", Link: "types/d2d883a8-b765-480c-a61a-29ff661c6690" },
    {
      name: "COLLECTIONS",
      Link: "items/filter",
    },
  ],
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [showCart, setShowCart] = useState(false);

  const { cartItems } = useContext(ShoppingCartProductContext);

  function handleCartClick() {
    setShowCart(!showCart);
  }

  function handleCartClose() {
    setShowCart(false);
  }

  function handleLinkClick() {
    setOpen(false); // Ensure the menu closes when a link is clicked
  }

  return (
    <div className="bg-white">
      {/* Mobile menu */}
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40 lg:hidden"
          onClose={() => setOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-full flex-col overflow-y-auto bg-white pb-12 shadow-xl">
                <div className="flex items-center justify-end px-4 py-5 mt-24">
                  {/* Close Button */}
                  <button
                    type="button"
                    className="text-gray-400"
                    onClick={() => setOpen(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="space-y-0  border-gray-200">
                  {navigation.pages.map((page) => (
                    <div key={page.name} className="border-t border-gray-200">
                      <Link
                        to={page.Link}
                        className="block p-4 font-medium text-gray-900"
                        onClick={handleLinkClick}
                      >
                        {page.name}
                      </Link>
                    </div>
                  ))}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      <header className="fixed top-0 w-full z-50 bg-white shadow">
        <p className="flex h-10 items-center justify-center bg-red-600 px-4 text-sm font-medium text-white sm:px-6 lg:px-8">
          S/S 25 is on! Free shipping
        </p>

        <nav
          aria-label="Top"
          className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
        >
          <div className="border-b border-gray-200">
            <div className="flex h-16 items-center">
              <button
                type="button"
                className="relative rounded-md bg-white p-2 text-gray-400 lg:hidden"
                onClick={() => setOpen(true)}
              >
                <span className="sr-only">Open menu</span>
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              </button>

              {/* Logo */}
              <div className="ml-4 flex lg:ml-0">
                <Link to="/">
                  <img
                    className="h-8 w-auto"
                    src="https://genuins.com/cdn/shop/files/Logo3_7f96191a-c2da-417b-acdd-cc2a62d649ee.jpg?v=1630519544"
                    alt=""
                  />
                </Link>
              </div>

              {/* Flyout menus */}
              <Popover.Group className="hidden lg:ml-8 lg:block lg:self-stretch">
                <div className="flex h-full space-x-8">
                  {navigation.pages.map((page) => (
                    <Link
                      key={page.name}
                      to={page.Link}
                      className="flex items-center text-sm font-medium text-gray-700 hover:text-gray-800"
                      onClick={handleLinkClick} // Ensure menu closes on link click
                    >
                      {page.name}
                    </Link>
                  ))}
                </div>
              </Popover.Group>

              <div className="ml-auto flex items-center">
                {/* Search */}
                {/* <div className="flex lg:ml-6">
                  <a href="#" className="p-2 text-gray-400 hover:text-gray-500">
                    <span className="sr-only">Search</span>
                    <MagnifyingGlassIcon className="h-6 w-6" aria-hidden="true" />
                  </a>
                </div> */}

                {/* Cart */}
                <div className="ml-4 flow-root lg:ml-6">
                  <button
                    onClick={handleCartClick}
                    className="group -m-2 flex items-center p-2"
                  >
                    <ShoppingBagIcon
                      className="h-6 w-6 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                      aria-hidden="true"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700 group-hover:text-gray-800">
                      {cartItems.length}
                    </span>
                    <span className="sr-only">items in cart, view bag</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </header>
      <div className="pt-28">
        <ShoppingCart showCart={showCart} onClose={handleCartClose} />
      </div>
    </div>
  );
}
